import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ConversationScore {
  id: string;
  user_id: string;
  conversation_type: 'voice' | 'chat';
  fluency_score: number;
  vocabulary_score: number;
  confidence_score: number;
  overall_score: number;
  message_count: number;
  duration_seconds: number | null;
  created_at: string;
}

export interface ProgressMetrics {
  averageScore: number;
  totalConversations: number;
  streak: number;
  recentScores: ConversationScore[];
  improvement: number; // percentage change
  metrics: {
    fluency: number;
    vocabulary: number;
    confidence: number;
  };
}

export const useProgress = () => {
  const [progress, setProgress] = useState<ProgressMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadProgress = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setProgress(null);
        return;
      }

      // Get conversation scores from the last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const [{ data: scores, error: scoresError }, { count }] = await Promise.all([
        supabase
          .from('conversation_scores')
          .select('*')
          .eq('user_id', user.id)
          .gte('created_at', thirtyDaysAgo.toISOString())
          .order('created_at', { ascending: false })
          .limit(10),
        supabase
          .from('conversation_scores')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id),
      ]);

      if (scoresError) {
        // Table might not exist yet, return default values
        console.log('Scores table not ready:', scoresError.message);
        setProgress({
          averageScore: 0,
          totalConversations: 0,
          streak: 0,
          recentScores: [],
          improvement: 0,
          metrics: { fluency: 0, vocabulary: 0, confidence: 0 }
        });
        return;
      }

      const recentScores: ConversationScore[] = (scores ?? []).map((row) => ({
        id: row.id,
        user_id: row.user_id,
        conversation_type: row.conversation_type === "chat" ? "chat" : "voice",
        fluency_score: row.fluency_score,
        vocabulary_score: row.vocabulary_score,
        confidence_score: row.confidence_score,
        overall_score: row.overall_score,
        message_count: row.message_count,
        duration_seconds: row.duration_seconds,
        created_at: row.created_at ?? "",
      }));
      
      // Calculate metrics
      const windowCount = recentScores.length;
      const totalConversations = Math.max(count ?? windowCount, windowCount);
      
      if (windowCount === 0) {
        setProgress({
          averageScore: 0,
          totalConversations,
          streak: 0,
          recentScores: [],
          improvement: 0,
          metrics: { fluency: 0, vocabulary: 0, confidence: 0 }
        });
        return;
      }

      const avgFluency = recentScores.reduce((sum, s) => sum + s.fluency_score, 0) / windowCount;
      const avgVocab = recentScores.reduce((sum, s) => sum + s.vocabulary_score, 0) / windowCount;
      const avgConfidence = recentScores.reduce((sum, s) => sum + s.confidence_score, 0) / windowCount;
      const avgOverall = recentScores.reduce((sum, s) => sum + s.overall_score, 0) / windowCount;

      // Calculate improvement (compare first half to second half)
      let improvement = 0;
      if (windowCount >= 4) {
        const midpoint = Math.floor(windowCount / 2);
        const recentHalf = recentScores.slice(0, midpoint);
        const olderHalf = recentScores.slice(midpoint);
        
        const recentAvg = recentHalf.reduce((sum, s) => sum + s.overall_score, 0) / recentHalf.length;
        const olderAvg = olderHalf.reduce((sum, s) => sum + s.overall_score, 0) / olderHalf.length;
        
        if (olderAvg > 0) {
          improvement = Math.round(((recentAvg - olderAvg) / olderAvg) * 100);
        }
      }

      // Calculate streak (simplified - just based on consecutive days with activity)
      const streak = calculateStreak(recentScores);

      setProgress({
        averageScore: Math.round(avgOverall),
        totalConversations,
        streak,
        recentScores: recentScores.reverse(), // Oldest first for chart
        improvement,
        metrics: {
          fluency: Math.round(avgFluency),
          vocabulary: Math.round(avgVocab),
          confidence: Math.round(avgConfidence)
        }
      });

    } catch (err) {
      console.error('Error loading progress:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const calculateStreak = (scores: ConversationScore[]): number => {
    if (scores.length === 0) return 0;
    
    const dates = scores.map(s => new Date(s.created_at).toDateString());
    const uniqueDates = [...new Set(dates)];
    
    let streak = 1;
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    
    // Check if there's activity today or yesterday
    if (!uniqueDates.includes(today) && !uniqueDates.includes(yesterday)) {
      return 0;
    }
    
    // Count consecutive days
    for (let i = 0; i < uniqueDates.length - 1; i++) {
      const current = new Date(uniqueDates[i]);
      const next = new Date(uniqueDates[i + 1]);
      const diffDays = Math.round((current.getTime() - next.getTime()) / 86400000);
      
      if (diffDays === 1) {
        streak++;
      } else {
        break;
      }
    }
    
    return streak;
  };

  const saveScore = async (score: Omit<ConversationScore, 'id' | 'user_id' | 'created_at'>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('conversation_scores')
        .insert({
          user_id: user.id,
          ...score
        });

      if (error) {
        console.error('Error saving score:', error);
        return;
      }

      // Reload progress after saving
      await loadProgress();
    } catch (err) {
      console.error('Error saving score:', err);
    }
  };

  useEffect(() => {
    loadProgress();
  }, []);

  return { progress, loading, error, refresh: loadProgress, saveScore };
};
